import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Plus, Trash, X } from "@phosphor-icons/react";

const CHANNELS = ["whatsapp", "instagram", "facebook", "tiktok", "guidelines"];
const KINDS = ["message", "caption", "story", "reel", "video_idea", "post", "rule"];

export default function AdminContent() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ channel: "whatsapp", kind: "message", title_en: "", title_tr: "", body_en: "", body_tr: "" });

  const load = () => api.get("/academy").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/academy", form);
      toast.success("Content added");
      setOpen(false);
      setForm({ channel: "whatsapp", kind: "message", title_en: "", title_tr: "", body_en: "", body_tr: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    await api.delete(`/admin/academy/${id}`);
    toast.success("Deleted");
    load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-4xl">{t.admin.content}</h1>
        <button data-testid="open-add-content-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#f4a261] text-[#0b1521] text-sm">
          <Plus size={14} weight="bold" /> Add content
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`content-item-${it.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/50">{it.channel} · {it.kind}</div>
                <h3 className="font-serif-display text-xl mt-1">{it.title_en}</h3>
              </div>
              <button data-testid={`delete-content-${it.id}`} onClick={() => remove(it.id)} className="text-white/60 hover:text-red-300"><Trash size={16} /></button>
            </div>
            <p className="text-sm text-white/70 mt-3 line-clamp-3">{it.body_en}</p>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-2xl rounded-3xl bg-[#122031] p-8 border border-white/10 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/20 flex items-center justify-center"><X size={14} /></button>
            <h2 className="font-serif-display text-3xl">Add content</h2>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="content-channel" value={form.channel} onChange={set("channel")} className="input">
                  {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select data-testid="content-kind" value={form.kind} onChange={set("kind")} className="input">
                  {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <input data-testid="content-title-en" required placeholder="Title (EN)" value={form.title_en} onChange={set("title_en")} className="input" />
              <input data-testid="content-title-tr" required placeholder="Başlık (TR)" value={form.title_tr} onChange={set("title_tr")} className="input" />
              <textarea data-testid="content-body-en" required placeholder="Body (EN)" rows={4} value={form.body_en} onChange={set("body_en")} className="input resize-none" />
              <textarea data-testid="content-body-tr" required placeholder="İçerik (TR)" rows={4} value={form.body_tr} onChange={set("body_tr")} className="input resize-none" />
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full border border-white/20 text-sm">{t.common.cancel}</button>
                <button data-testid="submit-content-btn" type="submit" className="px-5 py-2.5 rounded-full bg-[#f4a261] text-[#0b1521] text-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .input { width:100%; padding:11px 14px; border-radius:10px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.15); color:white; font-size:14px; outline:none; }
        .input:focus { border-color:#f4a261; }
      `}</style>
    </div>
  );
}
